import React, { ChangeEvent, useEffect, useState } from "react";
import {
  Box,
  Button,
  Container,
  PaginationItem,
  Stack,
} from "@mui/material";
import Pagination from "@mui/material/Pagination";
import { useHistory } from "react-router-dom";
import RemoveRedEyeIcon from "@mui/icons-material/RemoveRedEye";
import ShoppingCartOutlinedIcon from "@mui/icons-material/ShoppingCartOutlined";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import { sweetTopSuccessAlert } from "../../../lib/sweetAlert";
import { Dispatch } from "@reduxjs/toolkit";
import { useDispatch, useSelector } from "react-redux";
import { setProducts } from "./slice";
import { Product, ProductInquiry } from "../../../lib/types/product";
import { createSelector } from "reselect";
import { retrieveProducts } from "./selector";
import { serverApi } from "../../../lib/config";
import { ProductGenre, ProductSort } from "../../../lib/enums/product.enum";
import ProductService from "../../services/ProductService";
import SearchIcon from "@mui/icons-material/Search";
import { CartItem } from "../../../lib/types/search";

const actionDispatch = (dispatch: Dispatch) => ({
  setProducts: (data: Product[]) => dispatch(setProducts(data)),
});

const productsRetriever = createSelector(retrieveProducts, (products) => ({
  products,
}));

interface ProductsProps {
  onAdd: (item: CartItem) => void;
}

const AUTHORS = [
  {
    name: "Margaret Atwood",
    genre: "Contemporary Novelist",
    works: "The Handmaid's Tale, Oryx and Crake",
    img: "https://images.unsplash.com/photo-1544005313-94ddf0286df2?auto=format&fit=crop&w=600&q=80",
  },
  {
    name: "Haruki Murakami",
    genre: "Modern Literature",
    works: "Norwegian Wood, Kafka on the Shore",
    img: "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?auto=format&fit=crop&w=600&q=80",
  },
  {
    name: "Gabriel García Márquez",
    genre: "Magic Realism",
    works: "One Hundred Years of Solitude",
    img: "https://images.unsplash.com/photo-1500648767791-00dcc994a43e?auto=format&fit=crop&w=600&q=80",
  },
  {
    name: "Fyodor Dostoevsky",
    genre: "Classic Literature",
    works: "Crime and Punishment, The Idiot",
    img: "https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?auto=format&fit=crop&w=600&q=80",
  },
];

const formatLabel = (format?: string) => {
  if (!format) return "STANDART size";
  if (format === "STANDARD") return "STANDART size";
  return `${format} size`;
};

export default function Products(props: ProductsProps) {
  const { onAdd } = props;
  const { setProducts } = actionDispatch(useDispatch());
  const { products } = useSelector(productsRetriever);
  const [pages, setPages] = useState(1);
  const [productSearch, setProductSearch] = useState<ProductInquiry>({
    page: 1,
    limit: 8,
    order: ProductSort.NEW,
    search: "",
  });
  const [searchText, setSearchText] = useState<string>("");
  const history = useHistory();

  useEffect(() => {
    const product = new ProductService();
    product
      .getProductsPage(productSearch)
      .then((data) => {
        setProducts(data.items);
        setPages(data.pages);
      })
      .catch((err) => console.log(err));
  }, [productSearch]);

  useEffect(() => {
    if (searchText === "") {
      setProductSearch((prev) => ({ ...prev, search: "", page: 1 }));
    }
  }, [searchText]);

  const searchGenreHandler = (genre?: ProductGenre) => {
    setProductSearch((prev) => ({
      ...prev,
      page: 1,
      productGenre: genre,
      productCollection: genre as any,
    }));
  };

  const searchOrderHandler = (order: ProductSort) => {
    setProductSearch((prev) => ({ ...prev, page: 1, order }));
  };

  const searchProductHandler = () => {
    setProductSearch((prev) => ({ ...prev, search: searchText, page: 1 }));
  };

  const paginationHandler = (_e: ChangeEvent<any>, value: number) => {
    setProductSearch((prev) => ({ ...prev, page: value }));
  };

  const chooseBookHandler = (id: string) => {
    history.push(`/products/${id}`);
  };

  return (
    <div className={"products booclub-products"}>
      <Container>
        <Stack flexDirection={"column"} alignItems={"center"}>
          <Box className="booclub-page-title">BooClub Books</Box>

          <Stack className="booclub-filter-bar" direction="row" alignItems="center">
            <Stack direction="row" className="genre-pills" spacing={1}>
              {Object.values(ProductGenre).map((genre) => {
                const active = productSearch.productGenre === genre;
                return (
                  <Button
                    key={genre}
                    className={active ? "pill active" : "pill"}
                    onClick={() => searchGenreHandler(genre)}
                  >
                    {genre}
                  </Button>
                );
              })}
            </Stack>

            <Stack direction="row" className="search-block" spacing={1}>
              <input
                type="search"
                className="input"
                placeholder="Type here"
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") searchProductHandler();
                }}
              />
              <Button
                className="single-button-search"
                variant="contained"
                endIcon={<SearchIcon />}
                onClick={searchProductHandler}
              >
                SEARCH
              </Button>
            </Stack>

            <Stack direction="row" className="sort-pills" spacing={1}>
              {[ProductSort.NEW, ProductSort.PRICE, ProductSort.VIEWS].map(
                (order) => (
                  <Button
                    key={order}
                    className={
                      productSearch.order === order ? "pill active" : "pill"
                    }
                    onClick={() => searchOrderHandler(order)}
                  >
                    {order}
                  </Button>
                )
              )}
            </Stack>
          </Stack>

          <Stack className="product-wrapper booclub-grid">
            {products.length !== 0 ? (
              products.map((product: Product) => {
                const imagePath = `${serverApi}/${product.productImages[0]}`;
                return (
                  <Stack
                    key={product._id}
                    className="product-card"
                    onClick={() => chooseBookHandler(product._id)}
                  >
                    <Stack
                      className="product-img"
                      sx={{ backgroundImage: `url(${imagePath})` }}
                    >
                      <div className="product-sale">
                        {formatLabel(product.productFormat)}
                      </div>
                      <div className="product-hover">
                        <Button
                          className="add-basket-btn"
                          variant="contained"
                          startIcon={<ShoppingCartOutlinedIcon />}
                          onClick={(e) => {
                            e.stopPropagation();
                            onAdd({
                              _id: product._id,
                              quantity: 1,
                              name: product.productName,
                              price: product.productPrice,
                              image: product.productImages[0],
                            });
                            sweetTopSuccessAlert("Added to basket", 700);
                          }}
                        >
                          Add to Basket
                        </Button>
                        <span className="hover-views">
                          <RemoveRedEyeIcon sx={{ fontSize: 16 }} />
                          {product.productViews}
                        </span>
                      </div>
                    </Stack>
                    <Box className={"product-desc"}>
                      <span className="product-title">{product.productName}</span>
                      <div className="price-row">
                        <span className="dollar">$</span>
                        <span>{product.productPrice}</span>
                      </div>
                    </Box>
                  </Stack>
                );
              })
            ) : (
              <Box className="no-data">Books are not available!</Box>
            )}
          </Stack>

          <Stack className="pagination-section">
            <Pagination
              count={pages}
              page={productSearch.page}
              renderItem={(item) => (
                <PaginationItem
                  components={{
                    previous: ArrowBackIcon,
                    next: ArrowForwardIcon,
                  }}
                  {...item}
                  color="secondary"
                />
              )}
              onChange={paginationHandler}
            />
          </Stack>
        </Stack>
      </Container>

      <div className="brands-logo famous-authors">
        <div className="family-brand">Our Famous Authors</div>
        <p className="authors-subtitle">The finest writers from around the world.</p>
        <div className="burak-wrapper authors-grid">
          {AUTHORS.map((author) => (
            <Stack className="author-card" key={author.name}>
              <div
                className="author-photo"
                style={{ backgroundImage: `url(${author.img})` }}
              />
              <strong className="author-name">{author.name}</strong>
              <span className="author-genre">{author.genre}</span>
              <span className="author-works">{author.works}</span>
            </Stack>
          ))}
        </div>
      </div>

      <div className="address">
        <Container>
          <Stack>
            <Box className="our-address">Our Locations</Box>
            <iframe
              title="BooClub location"
              style={{ marginTop: "60px", marginBottom: "60px" }}
              src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d25859.219637661423!2d128.60129279999998!3d35.88815774999999!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3565e1aa3b33a607%3A0xca08be7329f6a0ca!2sEXCO!5e0!3m2!1sen!2skr!4v1771668833177!5m2!1sen!2skr"
              referrerPolicy="no-referrer-when-downgrade"
              width="100%"
              height="500"
            ></iframe>
          </Stack>
        </Container>
      </div>
    </div>
  );
}
